import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Checkbox, Image, Loader, Table } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/AccommodationsTable.less';

import iconInfo from '../img/icon-info.svg';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import AccommodationsService from '../services/AccommodationsService';
import PopupService from '../services/PopupService';
import { ACCOMMODATION_TOGGLE_KEY } from '../services/AccommodationConstants';
import { translationLanguageNames } from './TranslationLanguageSelector';

/** TTS-group toggles; Content Translation (c2cTranslationToggle) always renders immediately after the last enabled one */
const TTS_GROUP_TOGGLE_KEYS = ['textToSpeechToggle', 'dictionaryToggle', 'translationToggle'];

const AccommodationsTable = observer((props) => {
  const { /* hideEdit, readOnly, */t } = props;

  const TranslationLanguageModal = SatCoreComponent('TranslationLanguageModal');

  const {
    accommodationsManager
  } = useContext(MobXProviderContext);

  const [hideTable, setHideTable] = useState(false);
  const [loadingAccommodationsTable, setLoadingAccommodationsTable] = useState(false);
  // Optimistic per-student picker selections, keyed by `${rosterField}:${rosterItem.id}`
  const [translationLanguageSelections, setTranslationLanguageSelections] = useState(new Map());
  const [translationLanguageNamesMap, setTranslationLanguageNamesMap] = useState(translationLanguageNames);
  const [localeNamesMap, setLocaleNamesMap] = useState({});
  // null until /viewMyPublisherOptions resolves
  const [publisherOptions, setPublisherOptions] = useState(null);
  // Which student/column the Translation Language modal is open for: { rosterItem, toggleKey } | null
  const [languageModalTarget, setLanguageModalTarget] = useState(null);

  // Spot Translate (ReadSpeaker/TextHelp) requires a publisher key; C2C Translate requires publisher AI
  const hasSpotTranslate = !!(publisherOptions?.hasWebReaderId || publisherOptions?.hasTextHelpId);
  const hasC2CTranslate = !!publisherOptions?.aiEnabled;

  // Spot Translation picker (original Translate tool, LSN_/ASM_TRANSLATION) → user.spotTranslationLocaleId
  const showSpotTranslationLocaleSelector = hasSpotTranslate && accommodationsManager.hasAccommodationLocaleSelector;
  // Content Translation picker (C2C Translate, LSN_/ASM_C2C_TRANSLATION) → user.localeId
  const showC2CTranslationLocaleSelector = hasC2CTranslate && accommodationsManager.hasAccommodationLocaleSelector;
  const showAccommodationLocaleSelector = showSpotTranslationLocaleSelector || showC2CTranslationLocaleSelector;

  /**
   * Which toggle columns carry a per-student language picker, and what each one edits.
   * - translationToggle (Spot Translation): original Translate tool → SPOT_TRANSLATION_LOCALE_ID
   *     null → "Default or All" (no fixed target; the student picks in the lesson)
   * - c2cTranslationToggle (Content Translation): C2C Translate → LOCALE_ID (always seeded, default 'en')
   * Both pickers list the publisher's translation languages.
   */
  const LOCALE_SELECTOR_BY_TOGGLE = {
    c2cTranslationToggle: {
      // No value means the publisher default (English)
      clearLabelKey: null,
      headerLine1Key: 'contentTranslationLine1',
      headerLine2Key: 'contentTranslationLine2',
      modalTextKey: 'contentTranslationModalText',
      rosterField: 'localeId',
      show: showC2CTranslationLocaleSelector,
      update: (userId, localeId) => AccommodationsService.updateUserLocale(userId, localeId)
    },
    translationToggle: {
      // No value means no fixed target: the student picks in the lesson ("Default or All")
      clearLabelKey: 'spotTranslationDefaultLabel',
      headerLine1Key: 'spotTranslationLine1',
      headerLine2Key: 'spotTranslationLine2',
      modalTextKey: 'spotTranslationModalText',
      rosterField: 'spotTranslationLocaleId',
      show: showSpotTranslationLocaleSelector,
      update: (userId, localeId) => AccommodationsService.updateUserSpotTranslationLocale(userId, localeId)
    }
  };

  const getLocaleSelectorConfig = (toggleKey) => {
    const selectorConfig = LOCALE_SELECTOR_BY_TOGGLE[toggleKey];
    return selectorConfig?.show ? selectorConfig : null;
  };

  // Fetch publisher options to determine which translation columns/pickers should be shown
  useEffect(() => {
    const loadPublisherOptions = async () => {
      const options = await AccommodationsService.fetchPublisherOptions();
      setPublisherOptions(options || {});
    };
    loadPublisherOptions();
  }, []);

  // Fetch publisher translation language names (only if a locale selector is shown)
  useEffect(() => {
    if (!showAccommodationLocaleSelector) {
      return;
    }

    const loadTranslations = async () => {
      const languages = await AccommodationsService.fetchTranslationLanguages();
      if (languages && Object.keys(languages).length > 0) {
        setTranslationLanguageNamesMap(languages);
      }
    };
    loadTranslations();
  }, [showAccommodationLocaleSelector]);

  // Fetch locale names (display fallback for a stored locale that is not a publisher translation)
  useEffect(() => {
    if (!showAccommodationLocaleSelector) {
      return;
    }

    const loadLocales = async () => {
      const locales = await AccommodationsService.fetchLocaleNames();
      if (locales && Object.keys(locales).length > 0) {
        setLocaleNamesMap(locales);
      }
    };
    loadLocales();
  }, [showAccommodationLocaleSelector]);

  /**
   * Effective config, derived from the satellite config:
   * - Spot Translation (`translationToggle`) is only shown when the publisher has a ReadSpeaker or
   *   TextHelp key. Gating is applied once publisher options are known so the column does not
   *   flash in/out while loading.
   * - Content Translation (`c2cTranslationToggle`) always renders immediately after the TTS group
   *   (Text To Speech, Dictionary, Spot Translation) — i.e. right after whichever of those is the
   *   last one enabled — whatever order the satellite listed them in.
   */
  const config = (() => {
    const baseConfig = AccommodationsService.ACCOMMODATIONS_CONFIG();
    const hideSpotTranslation = !!publisherOptions && !hasSpotTranslate;
    const effectiveConfig = { ...baseConfig };

    const sectionKeysWithToggles = ['assessmentSection', 'lessonSection']
      .filter((sectionKey) => !!baseConfig[sectionKey]?.toggles);
    for (const sectionKey of sectionKeysWithToggles) {
      const nextToggles = { ...baseConfig[sectionKey].toggles };
      if (hideSpotTranslation && nextToggles.translationToggle) {
        nextToggles.translationToggle = false;
      }

      // Rebuild key order so c2cTranslationToggle directly follows the last enabled TTS-group toggle
      const keys = Object.keys(nextToggles);
      const enabledTtsGroupKeys = TTS_GROUP_TOGGLE_KEYS.filter((key) => nextToggles[key]);
      const anchorKey = enabledTtsGroupKeys.length
        ? keys.filter((key) => enabledTtsGroupKeys.includes(key)).pop()
        : null;
      const anchorIndex = anchorKey ? keys.indexOf(anchorKey) : -1;
      const c2cIndex = keys.indexOf('c2cTranslationToggle');
      const needsReorder = !!nextToggles.c2cTranslationToggle && anchorIndex !== -1 && c2cIndex !== anchorIndex + 1;

      const orderedToggles = needsReorder
        ? keys
          .filter((key) => key !== 'c2cTranslationToggle')
          .flatMap((key) => (key === anchorKey ? [key, 'c2cTranslationToggle'] : [key]))
          .reduce((acc, key) => {
            acc[key] = nextToggles[key];
            return acc;
          }, {})
        : nextToggles;

      effectiveConfig[sectionKey] = { ...baseConfig[sectionKey], toggles: orderedToggles };
    }
    return effectiveConfig;
  })();

  const handleToggleAccommodation = async (_event, { checked }, {
    rosterItem,
    sectionKey,
    toggleKey
  } = {}) => {
    setLoadingAccommodationsTable(true);

    const showingAllSections = config.assessmentSection?.show && config.lessonSection?.show;
    const shouldRefreshTableData = !rosterItem || (showingAllSections && config.treatAllSectionsAsOne);

    if (shouldRefreshTableData) {
      setHideTable(true);
    }
    const shouldRemove = !checked;
    const sectionKeys = config.treatAllSectionsAsOne ? ['assessmentSection', 'lessonSection'] : [sectionKey];
    await updateStudentOrClassroomAccommodationOption({ rosterItem, sectionKeys, shouldRemove, toggleKey });

    if (!hideTable) {
      setHideTable(true);
    }
    if (shouldRefreshTableData) {
      setTimeout(async () => {
        await accommodationsManager.fetchAccommodationsRoster();
        setLoadingAccommodationsTable(false);
        setHideTable(false);
      }, 1500);
    } else {
      setLoadingAccommodationsTable(false);
      setHideTable(false);
    }
  };

  const updateStudentOrClassroomAccommodationOption = async ({
    rosterItem,
    sectionKeys,
    shouldRemove,
    toggleKey
  } = {}) => {
    const toggleGroupsMap = new Map(config?.toggleGroups || []);
    for (const sectionKey of sectionKeys) {
      const mainAccommodationId = ACCOMMODATION_TOGGLE_KEY[sectionKey][toggleKey];

      const val = toggleGroupsMap?.get(mainAccommodationId) || [];
      const toggleGroupArray = Array.isArray(val) ? val : [];
      if (!toggleGroupArray?.length) {
        // eslint-disable-next-line no-await-in-loop
        await accommodationsManager.updateStudentOrClassroomAccommodationOption(
          rosterItem?.id, mainAccommodationId, shouldRemove
        );
      } else {
        for (const accommodationId of toggleGroupArray) {
          // eslint-disable-next-line no-await-in-loop
          await accommodationsManager.updateStudentOrClassroomAccommodationOption(
            rosterItem?.id, accommodationId, shouldRemove
          );
        }
      }
    }
  };

  const getTableCellAdditionalClassNames = (index) => {
    let additionalClassNames = '';
    if (config?.cellDividerClassName === 'section-divider' && index === 0) {
      additionalClassNames = classNames(additionalClassNames, 'section-divider');
    } else if (config?.cellDividerClassName !== 'section-divider') {
      additionalClassNames = classNames(additionalClassNames, config.cellDividerClassName);
    }
    return additionalClassNames;
  };

  const normalizeTranslationLanguageValue = (value) => {
    if (!value || value === 'All') {
      return 'en';
    }
    // If it's a comma-separated string, take the first value only (single select)
    if (typeof value === 'string' && value.includes(',')) {
      return value.split(',')[0].trim();
    }
    if (Array.isArray(value)) {
      return value[0] || 'en';
    }
    return value;
  };

  const getSelectionKey = (selectorConfig, rosterItem) => `${selectorConfig.rosterField}:${rosterItem.id}`;

  // Pickers with a clear option keep `null` as a real value; others coerce empty to 'en'
  const normalizeSelectorValue = (selectorConfig, value) => {
    if (selectorConfig.clearLabelKey) {
      return value ? normalizeTranslationLanguageValue(value) : null;
    }
    return normalizeTranslationLanguageValue(value);
  };

  const getSelectedTranslationLanguage = (selectorConfig, rosterItem) => {
    const selectionKey = getSelectionKey(selectorConfig, rosterItem);
    if (translationLanguageSelections.has(selectionKey)) {
      return translationLanguageSelections.get(selectionKey);
    }
    return normalizeSelectorValue(selectorConfig, rosterItem[selectorConfig.rosterField]);
  };

  const getTranslationLanguageDisplayValue = (selectorConfig, selectedLanguage) => {
    if (!selectedLanguage) {
      return selectorConfig.clearLabelKey
        ? t(selectorConfig.clearLabelKey)
        : (localeNamesMap['en'] || 'English');
    }
    return localeNamesMap[selectedLanguage] || translationLanguageNamesMap[selectedLanguage] || selectedLanguage;
  };

  // Called from the Translation Language modal on Confirm
  const handleTranslationLanguageSelect = async ({ language, rosterItem, selectorConfig }) => {
    if (!selectorConfig?.show) {
      return;
    }

    const selectionKey = getSelectionKey(selectorConfig, rosterItem);

    // Store previous value for rollback
    const previousSelection = translationLanguageSelections.has(selectionKey)
      ? translationLanguageSelections.get(selectionKey)
      : normalizeSelectorValue(selectorConfig, rosterItem[selectorConfig.rosterField]);

    const setSelection = (value) => {
      setTranslationLanguageSelections((previousSelections) => {
        const nextSelections = new Map(previousSelections);
        nextSelections.set(selectionKey, value);
        return nextSelections;
      });
    };

    // Optimistically update UI
    setSelection(language);

    // Update backend
    try {
      const result = await selectorConfig.update(rosterItem.id, language);

      if (!result.success) {
        console.error(`Failed to update ${selectorConfig.rosterField}:`, result.message);
        setSelection(previousSelection); // Rollback optimistic update on failure
      }
    } catch (error) {
      console.error(`Error updating ${selectorConfig.rosterField}:`, error);
      setSelection(previousSelection); // Rollback optimistic update on error
    }
  };

  const shouldRenderLocaleSelector = ({ rosterItem, toggleKey } = {}) => {
    return !!rosterItem && !!getLocaleSelectorConfig(toggleKey);
  };

  /**
   * Link that opens the Translation Language modal for one student/column.
   * Disabled while the accommodation toggle is OFF (the language has no effect then).
   */
  const renderTranslationLanguageSelector = ({ accommodationOn = true, rosterItem, toggleKey } = {}) => {
    const selectorConfig = getLocaleSelectorConfig(toggleKey);
    if (!selectorConfig) {
      return null;
    }
    const selectedLanguage = getSelectedTranslationLanguage(selectorConfig, rosterItem);
    const displayValue = getTranslationLanguageDisplayValue(selectorConfig, selectedLanguage);

    return (
      <button
        className={classNames('translation-language-trigger', {
          'clickable link-color': accommodationOn,
          'disabled': !accommodationOn
        })}
        disabled={!accommodationOn}
        onClick={() => setLanguageModalTarget({ rosterItem, toggleKey })}
        type='button'
      >
        {displayValue}
      </button>
    );
  };

  const renderTranslationLanguageModal = () => {
    if (!languageModalTarget) {
      return null;
    }
    const { rosterItem, toggleKey } = languageModalTarget;
    const selectorConfig = getLocaleSelectorConfig(toggleKey);
    if (!selectorConfig) {
      return null;
    }
    return (
      <TranslationLanguageModal
        clearOptionLabel={selectorConfig.clearLabelKey ? t(selectorConfig.clearLabelKey) : null}
        close={() => setLanguageModalTarget(null)}
        description={t(selectorConfig.modalTextKey)}
        languageNames={translationLanguageNamesMap}
        onConfirm={(language) => handleTranslationLanguageSelect({ language, rosterItem, selectorConfig })}
        open
        selectedLanguage={getSelectedTranslationLanguage(selectorConfig, rosterItem)}
        title={t('translationLanguage')} />
    );
  };

  const renderAccommodationsTable = () => {
    return (hideTable && loadingAccommodationsTable) ? <Loader active /> : (
      <>
        <Loader active={!hideTable && loadingAccommodationsTable} />
        <Table className='accommodations-table' striped>
          {config.showTopHeader && renderAccommodationsTopHeader()}
          <Table.Header>
            {renderAccommodationsTableHeader()}
          </Table.Header>
          <Table.Body>
            {renderAccommodationsTableBody()}
          </Table.Body>
        </Table>
        {renderTranslationLanguageModal()}
      </>
    );
  };

  const renderAccommodationsTopHeader = () => {
    // Use the effective (publisher-gated) config rather than the raw service config
    const assessmentSectionToggleCount = AccommodationsService.getConfigEnabledAccommodationToggleKeys({
      config, sectionKey: 'assessmentSection'
    }).length;
    const lessonSectionToggleCount = AccommodationsService.getConfigEnabledAccommodationToggleKeys({
      config, sectionKey: 'lessonSection'
    }).length;
    return (
      <Table.Header>
        <Table.Row className='accommodations-table-top-header'>
          <Table.Cell className='accommodations-cell-first-name firstName placeholder'>
            {/* placeholder */}
          </Table.Cell>
          <Table.Cell className='accommodations-cell-last-name lastName placeholder'>
            {/* placeholder */}
          </Table.Cell>

          {renderAccommodationsTopHeaderSection({
            sectionKey: 'assessmentSection', toggleCount: assessmentSectionToggleCount
          })}
          {renderAccommodationsTopHeaderSection({
            sectionKey: 'lessonSection', toggleCount: lessonSectionToggleCount
          })}
        </Table.Row>
      </Table.Header>
    );
  };

  /**
   * @param {{
   *   sectionKey: 'assessmentSection' | 'lessonSection'
   *   toggleCount: number
   * }}
   */
  const renderAccommodationsTopHeaderSection = ({ sectionKey, toggleCount } = {}) => {
    const cells = new Array(toggleCount).fill();

    const firstSectionCellIndex = 0;
    const lastSectionCellIndex = cells.length - 1;
    const sectionCellIndex = sectionKey === 'assessmentSection' ? lastSectionCellIndex : firstSectionCellIndex;
    cells[sectionCellIndex] = (
      <>
        {t(sectionKey)}
        {renderAccommodationsHeaderPopup({ content: t(`${sectionKey}Description`) })}
      </>
    );
    return (
      <>
        {cells.map((cellJsx, index) => {
          let additionalClassNames = getTableCellAdditionalClassNames(index)?.trim?.();
          additionalClassNames = classNames(sectionKey, {
            assessmentSectionLabel: index === firstSectionCellIndex || index === lastSectionCellIndex
          }, additionalClassNames);
          return (
            <Table.Cell key={`placeholder_${sectionKey}_${index}`}
              className={additionalClassNames}>
              {cellJsx}
            </Table.Cell>
          );
        })}
      </>
    );
  };

  const renderAccommodationsTableHeader = () => {
    let tableHeaderClassNames = 'accommodations-header-row';
    tableHeaderClassNames += config?.showTopHeader ? ' has-top-header' : '';
    return (
      <Table.Row className={tableHeaderClassNames} style={{ fontSize: config.tableHeaderFontSize || 'inherit' }}>
        {/* HEADER: FIRST NAME */}
        <Table.HeaderCell className='accommodations-cell-first-name firstName'>
          {t('firstName')}
        </Table.HeaderCell>
        {/* HEADER: LAST NAME */}
        <Table.HeaderCell className='accommodations-cell-last-name lastName'>
          {t('lastName')}
        </Table.HeaderCell>
        {/* HEADER: TOGGLES */}
        {renderAccommodationsTableHeaderRowSection({ sectionKey: 'assessmentSection' })}
        {renderAccommodationsTableHeaderRowSection({ sectionKey: 'lessonSection' })}
      </Table.Row>
    );
  };

  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  const renderAccommodationsTableHeaderRowSection = ({ sectionKey } = {}) => {
    return config[sectionKey].show && (
      Object.keys(config[sectionKey].toggles).map((toggleKey, index) => {
        const infoIconLabelTranslationKey = `${toggleKey}InfoIconLabel`; // e.g. 'contentLanguageToggleInfoIconLabel'
        const infoIconLabelTranslationValue = t(infoIconLabelTranslationKey, ''); // e.g. 'Coming Fall 2024'

        // only show an info icon for a toggle label if it has a proper translation value associated with it
        // else if `infoIconLabelTranslationValue` is empty or equals the translation key, do not show the info icon
        const hasInfoIcon = infoIconLabelTranslationValue && (
          infoIconLabelTranslationKey !== infoIconLabelTranslationValue
        );

        const additionalClassNames = getTableCellAdditionalClassNames(index);
        const localeSelectorConfig = getLocaleSelectorConfig(toggleKey);
        const showStackedContentTranslationLabel = !!localeSelectorConfig;
        return config[sectionKey].toggles[toggleKey] && (
          <Table.HeaderCell key={toggleKey}
            className={classNames(
              'accommodations-header-cell',
              `accommodations-cell-${index}`,
              sectionKey,
              toggleKey,
              additionalClassNames,
              { 'has-locale-selector': showStackedContentTranslationLabel }
            )}>
            {showStackedContentTranslationLabel ? (
              <span className='content-translation-header'>
                <span className='content-translation-header-label'>
                  {t(localeSelectorConfig.headerLine1Key)}
                  <br />
                  {t(localeSelectorConfig.headerLine2Key)}
                </span>
                {hasInfoIcon && renderAccommodationsHeaderPopup({ content: infoIconLabelTranslationValue })}
              </span>
            ) : (
              <>
                {t(toggleKey)}
                {hasInfoIcon && renderAccommodationsHeaderPopup({ content: infoIconLabelTranslationValue })}
              </>
            )}
          </Table.HeaderCell>
        );
      })
    );
  };

  const renderAccommodationsHeaderPopup = ({ content, position = 'top center' }) => {
    return PopupService.renderPopup({
      className: 'toolbar-item-popup',
      content,
      position,
      trigger: <Image alt='' className='info-icon-pointable' spaced src={iconInfo} />
    });
  };

  const renderAccommodationsTableBody = () => {
    return (
      <>
        {accommodationsManager.currentAccommodationsRosterArray?.length ? (
          <Table.Row>
            {/* BODY: TOP (summary) ROW */}
            <Table.Cell>{t('selectAllStudents')}</Table.Cell>
            <Table.Cell>{/* placeholder */}</Table.Cell>
            {renderAccommodationsTableBodySummaryRowSection({ sectionKey: 'assessmentSection' })}
            {renderAccommodationsTableBodySummaryRowSection({ sectionKey: 'lessonSection' })}
          </Table.Row>
        ) : (
          <Table.Row>
            <Table.Cell className='accommodations-cell zeroState'>{t('accommodationsZeroState')}</Table.Cell>
          </Table.Row>
        )}
        {/* BODY: STUDENT ROWS */}
        {accommodationsManager.currentAccommodationsRosterArray?.map((rosterItem) => {
          return (
            <Table.Row key={rosterItem.id}>
              {renderTableBodyPopupCell({ cellKey: 'firstName', content: rosterItem.firstName })}
              {renderTableBodyPopupCell({ cellKey: 'lastName', content: rosterItem.lastName })}
              {renderAccommodationsTableBodyRowSection({ rosterItem, sectionKey: 'assessmentSection' })}
              {renderAccommodationsTableBodyRowSection({ rosterItem, sectionKey: 'lessonSection' })}
            </Table.Row>
          );
        })}
      </>
    );
  };

  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  const renderAccommodationsTableBodySummaryRowSection = ({ sectionKey } = {}) => {
    if (!config[sectionKey].show) {
      return null;
    }
    const toggleKeys = AccommodationsService.getConfigEnabledAccommodationToggleKeys({
      config, sectionKey
    });

    const rosterItems = accommodationsManager.currentAccommodationsRosterArray;

    const offAccommodationsForAll = AccommodationsService.getOffAccommodationsForAll({
      config, rosterItems, sectionKey
    });

    return toggleKeys.map((toggleKey, index) => {
      const additionalClassNames = getTableCellAdditionalClassNames(index);
      const isAccommodationOn = !offAccommodationsForAll.includes(toggleKey);
      return (
        <Table.Cell key={`all_${toggleKey}`}
          className={classNames(
            'accommodations-cell',
            `accommodations-cell-${index}`,
            'toggle',
            sectionKey,
            toggleKey,
            additionalClassNames,
            { 'locale-selector-column': !!getLocaleSelectorConfig(toggleKey) }
          )}>
          {renderToggler({
            checked: isAccommodationOn,
            sectionKey,
            toggleKey
          })}
        </Table.Cell>
      );
    });
  };

  const renderTableBodyPopupCell = ({ cellKey, content } = {}) => {
    return (
      <Table.Cell className={`accommodations-cell popup-cell ${cellKey}`}>
        {PopupService.renderPopup({
          content,
          offset: [-11, 0],
          position: 'top left',
          trigger: content
        })}
      </Table.Cell>
    );
  };

  /**
   * @param {{ sectionKey: 'assessmentSection' | 'lessonSection' }}
   */
  const renderAccommodationsTableBodyRowSection = ({ rosterItem, sectionKey } = {}) => {
    return config[sectionKey].show && (
      Object.keys(config[sectionKey].toggles).filter((toggleKey) => {
        return config[sectionKey].toggles[toggleKey];
      }).map((toggleKey, index) => {
        const additionalClassNames = getTableCellAdditionalClassNames(index);
        const isAccommodationOn = !rosterItem.offAccommodations?.includes(
          ACCOMMODATION_TOGGLE_KEY[sectionKey][toggleKey]
        );
        const showLocaleSelector = shouldRenderLocaleSelector({ rosterItem, sectionKey, toggleKey });
        return (
          <Table.Cell key={toggleKey}
            className={classNames(
              'accommodations-cell',
              `accommodations-cell-${index}`,
              'toggle',
              sectionKey,
              toggleKey,
              additionalClassNames,
              {
                'has-locale-selector': showLocaleSelector,
                'locale-selector-column': !!getLocaleSelectorConfig(toggleKey)
              }
            )}>
            {renderToggler({
              checked: isAccommodationOn,
              rosterItem,
              sectionKey,
              toggleKey
            })}
          </Table.Cell>
        );
      })
    );
  };

  const isTextToSpeechEnabled = ({ rosterItem, sectionKey } = {}) => {
    if (rosterItem) {
      return !rosterItem.offAccommodations?.includes(
        ACCOMMODATION_TOGGLE_KEY[sectionKey].textToSpeechToggle
      );
    }
    return !accommodationsManager.currentAccommodationsRosterArray.some((item) => {
      return item.offAccommodations?.includes(
        ACCOMMODATION_TOGGLE_KEY[sectionKey].textToSpeechToggle
      );
    });
  };

  const renderToggler = ({ checked, rosterItem, sectionKey, toggleKey } = {}) => {
    // Enable translationToggle and dictionaryToggle when textToSpeechToggle is enabled
    let finalShouldDisable = false;
    if (accommodationsManager.wordToolsTTSDependent) {
      const shouldDisable = !isTextToSpeechEnabled({ rosterItem, sectionKey }) &&
      (toggleKey === 'translationToggle' || toggleKey === 'dictionaryToggle');
      finalShouldDisable = shouldDisable;
    }

    const localeSelector = shouldRenderLocaleSelector({ rosterItem, sectionKey, toggleKey }) ?
      renderTranslationLanguageSelector({ accommodationOn: checked, rosterItem, toggleKey }) : null;

    // When any column in the row carries a language link, every toggler in that row reserves the
    // same language line (empty where there is no picker) so the toggles stay vertically aligned.
    const reserveLanguageRow = !!rosterItem && showAccommodationLocaleSelector;

    return (
      <div className={classNames('accommodations-table-column-toggler', toggleKey, {
        'has-language-row': reserveLanguageRow,
        'has-locale-selector': !!localeSelector,
        'locale-selector-column': !!getLocaleSelectorConfig(toggleKey)
      })}>
        <div className='toggle-on-or-off-wrapper'>
          <Checkbox
            className='on-or-off-toggler'
            defaultChecked={checked}
            disabled={finalShouldDisable}
            onChange={(event, { checked }) => handleToggleAccommodation(event, { checked }, {
              rosterItem, sectionKey, toggleKey
            })}
            toggle />
        </div>
        {reserveLanguageRow && (
          <div className='translation-language-row'>
            {localeSelector}
          </div>
        )}
      </div>
    );
  };
  return renderAccommodationsTable();
});
export default AccommodationsTable;

SatCoreRegister('AccommodationsTable', AccommodationsTable);
